import { AppConfig } from '@ltvco/refresh-lib/ctx';
import {
  useReportRedirect,
  EventType,
  parseAdvancedSearchURLParams as parseFormToUrl,
} from '@ltvco/refresh-lib/v2';
import { useContext } from 'react';

const createFilterParams = (params: Record<string, any>) => {
  const filterParams = {
    fullName: params.fullName || '',
    fName: params.fName || '',
    mName: params.mName || '',
    lName: params.lName || '',
    title: params.title || '',
    company: params.company || '',
    level: params.level || '',
    industry: params.industry || '',
    company_size_min: params.company_size_min || '1',
    company_size_max: params.company_size_max || '9',
    birthMonth: params.birthMonth || '',
    birthYear: params.birthYear || '',
    deathMonth: params.deathMonth || '',
    deathYear: params.deathYear || '',
    address: params.address || '',
    city: params.city || '',
    state: params.state || '',
    age: params.age || 0,
    isDeceased: Boolean(params.deathYear),
    location:
      params.location ||
      (params.city && params.state ? `${params.city}, ${params.state}` : ''),
    country: params.country || '',
    eventType: params.eventType || EventType.NONE,
    eventMonth: params.eventMonth || '',
    eventYear: params.eventYear || '',
    advancedSearch:
      params.advancedSearch === true || params.advancedSearch === 'true',
    selectedSources: { [params.source]: true },
    type: params.type || '',
  };

  return filterParams;
};

export const filterKeyMap: Record<string, string> = {
  fullname: 'fullName',
  fname: 'fName',
  mn: 'mName',
  ln: 'lName',
  title: 'title',
  company: 'company',
  industry: 'industry',
  level: 'level',
  companySizeMin: 'company_size_min',
  companSizeMax: 'company_size_max',
  birthMonth: 'birthMonth',
  birthYear: 'birthYear',
  deathMonth: 'deathMonth',
  deathYear: 'deathYear',
  address: 'address',
  isDeceased: 'isDeceased',
  location: 'location',
  country: 'country',
  city: 'city',
  state: 'state',
  age: 'age',
};

export const useUrlFilterNavigator = () => {
  const { redirectToSearchPersonUrlv2 } = useReportRedirect();
  const { routingUtils } = useContext(AppConfig);

  const getNonEmptyFilters = (): Record<string, string | string[]> => {
    const filters = getCurrentFilters();
    return Object.fromEntries(
      Object.entries(filters).filter(
        ([, value]) =>
          value !== null && value !== undefined && value !== '' && value !== '0'
      )
    );
  };

  const getCurrentFilters = (): Record<string, string | string[]> => {
    const urlParams = new URLSearchParams(routingUtils.location.search);
    const filters: Record<string, string | string[]> = {};

    Array.from(urlParams.entries()).forEach(([key, value]) => {
      const mappedKey = filterKeyMap[key] || key;
      if (filters[mappedKey]) {
        if (Array.isArray(filters[mappedKey])) {
          (filters[mappedKey] as string[]).push(value);
        } else {
          filters[mappedKey] = [filters[mappedKey] as string, value];
        }
      } else {
        filters[mappedKey] = value;
      }
    });

    return filters;
  };

  const applyFilters = (
    newFilters: Record<
      string,
      string | string[] | number | boolean | null | undefined
    >
  ) => {
    const baseParams = getCurrentFilters();

    Object.entries(newFilters).forEach(([key, value]) => {
      const mappedKey = filterKeyMap[key] || key;
      if (value === null || value === undefined) {
        delete baseParams[mappedKey];
      } else if (typeof value === 'number' || typeof value === 'boolean') {
        baseParams[mappedKey] = String(value);
      } else {
        baseParams[mappedKey] = value;
      }
    });
    const filterParams = createFilterParams(baseParams);

    const updatedParams = parseFormToUrl(filterParams);
    redirectToSearchPersonUrlv2(updatedParams);

    return updatedParams;
  };

  const resetFilters = () => {
    redirectToSearchPersonUrlv2({});
  };

  return {
    getNonEmptyFilters,
    getCurrentFilters,
    applyFilters,
    resetFilters,
  };
};
